import {
  ContractAdjustmentPayloadDtoAdjustmentTypeEnum,
  ContractResponseDto,
} from 'libs/pinnacle-sdk';
import * as yup from 'yup';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getLocalDateForMidnightUTC } from 'utils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const MAXIMUM_POLICY_EXTENSION_DAYS = 549;

export const getValidationSchema = (
  t: TFunction,
  contract?: ContractResponseDto
) =>
  yup.object().shape({
    // transaction-details
    transactionDate: yup
      .date()
      .required(
        t('validation.required', {
          field: t('contractAdjustment.effectiveDate'),
        })
      )
      .test(
        'effective-date-within-contract-range',
        t('contractAdjustment.validation.effectiveDateWithinContractRange'),
        function (value) {
          const inceptionDate = this.parent.inceptionDate;
          const expiryDate = this.parent.expiryDate;
          if (!value || !inceptionDate || !expiryDate) return true;

          const effectiveDate = dayjs(value);
          const inception = getLocalDateForMidnightUTC(inceptionDate);
          const expiry = getLocalDateForMidnightUTC(expiryDate);

          return (
            effectiveDate.isSameOrAfter(inception, 'day') &&
            effectiveDate.isSameOrBefore(expiry, 'day')
          );
        }
      ),
    inceptionDate: yup
      .date()
      .required(
        t('validation.required', { field: t('submission.inceptionDate') })
      ),
    expiryDate: yup
      .date()
      .required(t('validation.required', { field: t('submission.expiryDate') }))
      .test(
        'is-after-inception',
        t('validation.dateAfterInception', {
          field: t('submission.expiryDate'),
          date: t('submission.inceptionDate'),
        }),
        function (value) {
          const inceptionDate = this.parent.inceptionDate;
          if (!inceptionDate) return true;
          return dayjs(value).isAfter(
            getLocalDateForMidnightUTC(inceptionDate)
          );
        }
      )
      .test(
        'policy-extension-expiry-date-limit',
        t('contractAdjustment.validation.policyExtensionExpiryDateLimit'),
        function (value) {
          const adjustmentType = this.parent.adjustmentType;
          const inceptionDate = this.parent.inceptionDate;

          // Only apply this validation for Policy Extension adjustment type
          if (
            adjustmentType !==
            ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PolicyExtension
          ) {
            return true;
          }

          if (!value || !inceptionDate) return true;

          const expiry = dayjs(value);
          const inception = getLocalDateForMidnightUTC(inceptionDate);
          const daysDifference = expiry.diff(inception, 'day');

          return daysDifference <= MAXIMUM_POLICY_EXTENSION_DAYS;
        }
      ),
    adjustmentType: yup
      .string()
      .oneOf(
        Object.values(ContractAdjustmentPayloadDtoAdjustmentTypeEnum),
        t('validation.required', {
          field: t('contractAdjustment.adjustmentType'),
        })
      )
      .required(
        t('validation.required', {
          field: t('contractAdjustment.adjustmentType'),
        })
      ),
    // PPW fields - required for Premium Adjustment and Policy Extension
    premiumPaymentWarranty: yup.number().when('adjustmentType', {
      is: (val: string) =>
        val ===
          ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PremiumAdjustment ||
        val === ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PolicyExtension,
      then: () =>
        yup.number().required(
          t('validation.required', {
            field: t('contractAdjustment.premiumPaymentWarranty'),
          })
        ),
      otherwise: () => yup.number().optional(),
    }),
    underwritingComments: yup.string().optional(),

    // midterm-adjustments
    totalInsurableValue: yup.number().required(
      t('validation.required', {
        field: t('contractAdjustment.totalInsurableValue'),
      })
    ),
    // Layer Premium Adjustment Amount - conditional validation based on adjustment type
    layerPremiumAdjustmentAmount: yup
      .number()
      .required(
        t('validation.required', {
          field: t('contractAdjustment.layerPremiumAdjustmentValue'),
        })
      )
      .test(
        'no-negation-for-premium-adjustment',
        t('contractAdjustment.validation.noNegationAllowed'),
        function (value) {
          const adjustmentType = this.parent.adjustmentType;
          // For Premium Adjustment, value cannot be negative of original contract amount
          if (
            adjustmentType ===
              ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PremiumAdjustment ||
            adjustmentType ===
              ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PolicyExtension
          ) {
            if (value != null && contract?.layer?.premium != null) {
              return value !== -Number(contract.layer?.premium);
            }
          }
          return true;
        }
      )
      .test(
        'no-over-refund-for-premium-adjustment',
        t('contractAdjustment.validation.noOverRefundAllowed'),
        function (value) {
          const adjustmentType = this.parent.adjustmentType;
          if (
            (adjustmentType ===
              ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PremiumAdjustment ||
              adjustmentType ===
                ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PolicyExtension) &&
            value < 0
          ) {
            if (value != null && contract?.layer?.premium != null) {
              return value + Number(contract.layer?.premium) >= 0;
            }
          }
          return true;
        }
      ),
    cedingCommissionRate: yup.number().optional(),
    sourceBrokerageRate: yup.number().optional(),
    ereRate: yup.number().optional(),
    gstOnEre: yup.number().optional(),
    otherDeductions: yup.number().optional(),
    withholdingTaxRate: yup.number().optional(),
    brokerReference: yup.string().optional(),
    taxAndFeeAmount: yup.number().optional(),
    aitRate: yup.number().optional(),
    gstRateOnCedingCommission: yup.number().optional(),
    gstRateOnSourceBrokerage: yup.number().optional(),
    gstRateOnPremium: yup.number().optional(),
    binderShareSigned: yup.number().optional(),
  });

export interface ContractAdjustmentValues {
  transactionDate: Date;
  adjustmentType: ContractAdjustmentPayloadDtoAdjustmentTypeEnum;
  premiumPaymentWarranty: number;
  underwritingComments: string;
  brokerReference?: string;
  totalInsurableValue: number;
  layerPremiumAdjustmentAmount: number;
  cedingCommissionRate?: number;
  sourceBrokerageRate?: number;
  ereRate?: number;
  gstOnEre?: number;
  otherDeductions?: number;
  withholdingTaxRate?: number;
  taxAndFeeAmount?: number;
  aitRate?: number;
  gstRateOnCedingCommission?: number;
  gstRateOnSourceBrokerage?: number;
  expiryDate: Date;
  inceptionDate: Date;
  underwritingYear: number;
  gstRateOnPremium?: number;
  binderShareSigned?: number;
}
